import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusPill, STATUS_META } from "@/components/StatusPill";
import { MarkdownLite } from "@/components/MarkdownLite";
import { FileDiagnostics } from "@/components/FileDiagnostics";
import { OrderChat } from "@/components/OrderChat";
import { ProgressStepper, stepFromStatus, ORDER_STAGES } from "@/components/ProgressStepper";
import { DynoChart } from "@/components/DynoChart";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ArrowLeft, FileDown, UploadCloud, Sparkles, Save, Loader2, CheckCircle2, Library, Download, Gauge, AlertTriangle, UserCheck, UserPlus, Lock, Unlock, Hash, Layers, ShieldCheck, ShieldAlert, RefreshCw, Clock } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { langLabel, langFlag } from "@/lib/langLabel";

const ReliabilityPill = ({ reliability, deliveries }) => {
  if (reliability === null || reliability === undefined) {
    return <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-white/5 text-zinc-400">Nouveau</span>;
  }
  const color = reliability >= 90 ? "bg-signal-success/15 text-signal-success" : reliability >= 70 ? "bg-signal-processing/15 text-signal-processing" : "bg-signal-error/15 text-signal-error";
  return (
    <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${color}`} title={`${deliveries} livraison(s)`}>
      {reliability}% fiable · {deliveries} livr.
    </span>
  );
};

export const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInput = useRef(null);
  const [order, setOrder] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saveSolution, setSaveSolution] = useState(false);
  const [tunedWith, setTunedWith] = useState("");
  const [dyno, setDyno] = useState({ base_hp: "", gain_hp: "", base_nm: "", gain_nm: "" });
  const [savingDyno, setSavingDyno] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [applyingId, setApplyingId] = useState(null);

  const loadCandidates = () => api.get(`/admin/orders/${id}/candidates`)
    .then(({ data }) => setCandidates(data.candidates || []))
    .catch(() => setCandidates([]));

  const applySolution = async (solId, ref) => {
    if (!requireClaim()) return;
    setApplyingId(solId);
    try {
      await api.post(`/admin/orders/${id}/apply-solution`, { solution_id: solId });
      await load();
      await loadCandidates();
      toast.success(`Fichier ${ref || ""} livré au client`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setApplyingId(null); }
  };

  const [defectBusy, setDefectBusy] = useState(false);
  const reportDefect = async () => {
    if (!requireClaim()) return;
    const reason = window.prompt("Motif du défaut constaté sur ce fichier (note interne) :");
    if (reason === null) return;
    setDefectBusy(true);
    try {
      await api.post(`/admin/orders/${id}/report-defect`, { reason });
      await load(); await loadCandidates();
      toast.success("Défaut signalé — fiabilité du fichier mise à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setDefectBusy(false); }
  };
  const clearDefect = async () => {
    if (!requireClaim()) return;
    setDefectBusy(true);
    try {
      await api.post(`/admin/orders/${id}/report-defect`, { clear: true });
      await load(); await loadCandidates();
      toast.success("Défaut levé");
    } catch (e) { toast.error("Erreur"); }
    finally { setDefectBusy(false); }
  };

  const AUTO_SERVICES = ["fap_off", "egr_off", "adblue_off", "dtc_off", "o2_off"];
  const fullyAuto = order && order.services.every((s) => AUTO_SERVICES.includes(s)) && (order.ecu || "").trim();
  const autoEligible = order && (order.ecu || "").trim();

  const applyOrder = (data) => {
    setOrder(data);
    setNotes(data.admin_notes || "");
    setTunedWith(data.tuned_with || "");
    if (data.dyno) setDyno({
      base_hp: data.dyno.base_hp ?? "", gain_hp: data.dyno.gain_hp ?? "",
      base_nm: data.dyno.base_nm ?? "", gain_nm: data.dyno.gain_nm ?? "",
    });
  };

  const load = () => api.get(`/orders/${id}`).then(({ data }) => applyOrder(data));

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => applyOrder(data)).catch(() => navigate("/admin/orders"));
    // Opening a demande clears its notification immediately.
    api.post(`/orders/${id}/messages/read`)
      .then(() => window.dispatchEvent(new Event("notif-refresh")))
      .catch(() => {});
    loadCandidates();
  }, [id, navigate]);

  const saveDyno = async () => {
    if (!requireClaim()) return;
    setSavingDyno(true);
    try {
      await api.patch(`/admin/orders/${id}`, { dyno: {
        base_hp: Number(dyno.base_hp) || 0, gain_hp: Number(dyno.gain_hp) || 0,
        base_nm: Number(dyno.base_nm) || 0, gain_nm: Number(dyno.gain_nm) || 0,
      } });
      await load();
      toast.success("Dyno enregistré");
    } catch (e) { toast.error("Erreur"); }
    finally { setSavingDyno(false); }
  };

  const [stageNote, setStageNote] = useState("");
  const [stageBusy, setStageBusy] = useState(false);

  const setStage = async (step) => {
    if (!requireClaim()) return;
    setStageBusy(true);
    try {
      await api.post(`/admin/orders/${id}/stage`, { step, note: stageNote });
      await load();
      toast.success(`Étape : ${ORDER_STAGES[step].label}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setStageBusy(false);
  };

  useEffect(() => { if (order) setStageNote(order.progress_note || ""); }, [order?.id]);

  const assign = async (unassign = false) => {
    try {
      await api.post(`/admin/orders/${id}/assign`, { unassign });
      await load();
      toast.success(unassign ? "Vous vous êtes retiré du fichier" : "Vous avez pris ce fichier en charge");
      window.dispatchEvent(new Event("notif-refresh"));
    } catch (e) { toast.error("Erreur"); }
  };

  // Mandatory claim: no processing action is allowed until the current admin takes charge.
  const requireClaim = () => {
    if (!order || order.assigned_admin_email !== user?.email) {
      toast.error("Prenez d'abord la commande en charge pour la traiter");
      return false;
    }
    return true;
  };

  const setStatus = async (status) => {
    if (!requireClaim()) return;
    await api.patch(`/admin/orders/${id}`, { status });
    await load();
    toast.success(`Statut → ${STATUS_META[status].label}`);
  };

  const [closing, setClosing] = useState(false);
  const toggleClose = async () => {
    if (!requireClaim()) return;
    setClosing(true);
    try {
      await api.post(`/admin/orders/${id}/close`, { reopen: !!order.closed });
      await load();
      toast.success(order.closed ? "Demande rouverte" : "Demande clôturée");
    } catch (e) { toast.error("Erreur"); }
    finally { setClosing(false); }
  };

  const saveNotes = async () => {
    if (!requireClaim()) return;
    setSaving(true);
    try { await api.patch(`/admin/orders/${id}`, { admin_notes: notes }); toast.success("Note enregistrée"); }
    finally { setSaving(false); }
  };

  const downloadOriginal = async () => {
    const res = await api.get(`/orders/${id}/download/original`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = order.original_filename; a.click();
    window.URL.revokeObjectURL(url);
  };

  const uploadProcessed = async (file) => {
    if (!file) return;
    if (!requireClaim()) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    fd.append("save_as_solution", saveSolution ? "true" : "false");
    fd.append("tuned_with", tunedWith || "");
    try {
      const { data } = await api.post(`/admin/orders/${id}/processed-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success(data.saved_solution ? "Fichier envoyé + solution enregistrée (auto activé)" : "Fichier traité envoyé · commande terminée");
    } catch (e) { toast.error("Erreur d'envoi"); }
    finally { setUploading(false); }
  };

  const downloadSecondary = async () => {
    const res = await api.get(`/orders/${id}/download/secondary_original`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = order.secondary_filename || "boite.bin"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const uploadSecondary = async (file) => {
    if (!file) return;
    if (!requireClaim()) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("secondary", "true");
    try {
      await api.post(`/admin/orders/${id}/processed-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Fichier boîte modifié envoyé au client");
    } catch (e) { toast.error("Erreur d'envoi (boîte)"); }
    finally { setUploading(false); }
  };

  const cancelFile = async (which) => {
    const label = which === "secondary" ? "Boîte de vitesses" : "Moteur";
    if (!window.confirm(`Marquer le fichier ${label} comme NON réalisable ? Les crédits de ce fichier seront annulés/remboursés au client.`)) return;
    try {
      const { data } = await api.post(`/admin/orders/${id}/cancel-file`, { file: which });
      await load();
      toast.success(data.refunded ? `Fichier annulé · ${data.refunded} crédit(s) remboursé(s)` : "Fichier marqué non réalisable");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };


  const downloadReference = async () => {
    try {
      const res = await api.get(`/admin/solutions/${order.suggested_solution_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "reference_solution.bin"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Référence indisponible"); }
  };

  const [comboBusy, setComboBusy] = useState(false);
  const downloadCombination = async () => {
    try {
      const res = await api.get(`/admin/orders/${id}/combination/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = order.combination?.filename || "combination.bin"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Fichier combiné indisponible"); }
  };
  const deliverCombination = async () => {
    if (!requireClaim()) return;
    if (!window.confirm("Confirmez-vous la livraison de ce fichier pré-assemblé ?\n\nAssurez-vous d'avoir validé le checksum si nécessaire.")) return;
    setComboBusy(true);
    try {
      await api.post(`/admin/orders/${id}/deliver-combination`);
      await load();
      toast.success("Combinaison livrée · commande terminée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de livraison"); }
    finally { setComboBusy(false); }
  };

  const [reBusy, setReBusy] = useState(false);
  const reprocess = async () => {
    if (!requireClaim()) return;
    setReBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${id}/reprocess`);
      if (!data.changed) toast.info(data.detail || "Aucune solution ni combinaison trouvée dans la bibliothèque.");
      else if (data.applied_auto) toast.success("Livraison automatique effectuée ✅");
      else toast.success("⚡ Combinaison auto disponible — à valider et livrer");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de re-traitement"); }
    finally { setReBusy(false); }
  };

  if (!order) return <AppShell mode="admin"><div className="font-mono text-xs text-zinc-500">Chargement...</div></AppShell>;

  const locked = order.assigned_admin_email !== user?.email;

  const TOOL_LABELS = { dimsport: "Dimsport", autotuner: "Autotuner", flex: "Flex", kess: "KESS", autre: "Autre" };
  const MODE_LABELS = { obd: "OBD", bench: "Bench", boot: "Boot" };
  const ECU_TYPE_LABELS = { ecu_moteur: "ECU moteur", ecu_adblue: "ECU AdBlue", ecu_boite: "ECU boîte de vitesse", airbag: "Airbag", autre: "Autre" };
  const ecuTypeDisplay = order.ecu_type === "autre" ? (order.ecu_type_other || "Autre") : (ECU_TYPE_LABELS[order.ecu_type] || order.ecu_type);
  const info = [
    ["Client", order.user_name], ["Email", order.user_email],
    ["Langue du client", order.client_lang ? `${langFlag(order.client_lang)} ${langLabel(order.client_lang)}` : "—"],
    ["Type d'ECU", ecuTypeDisplay],
    ["Marque", order.vehicle_brand],
    ["Modèle", order.vehicle_model], ["Année", order.vehicle_year], ["Moteur", order.engine],
    ["ECU détecté", order.ecu_detected ? `${order.ecu_detected}${order.ecu_fuel ? " · " + ({ essence: "Essence", diesel: "Diesel" }[order.ecu_fuel] || "") : ""}` : null],
    ["ECU (saisi)", order.ecu], ["Boîte", order.gearbox],
    ["Outil de lecture", TOOL_LABELS[order.reading_tool] || order.reading_tool],
    ["Mode de lecture", MODE_LABELS[order.reading_mode] || order.reading_mode],
    ["Hardware Nr", order.hardware_nr], ["Software Nr", order.software_nr], ["Software upgrade", order.software_upgrade],
  ].filter(([, v]) => v);

  const inputCls = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none";

  return (
    <AppShell mode="admin">
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 font-mono text-xs uppercase tracking-widest">
        <ArrowLeft className="w-4 h-4" /> File de traitement
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// {order.id.slice(0, 8)}</div>
          <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{order.vehicle_brand} {order.vehicle_model}</h1>
          <div data-testid="admin-order-datetime" className="font-mono text-[11px] text-zinc-500 mt-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {fmtDateTime(order.created_at, "fr")}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {order.closed && (
            <span data-testid="closed-badge" className="px-3 py-1.5 rounded-sm bg-zinc-500/15 border border-zinc-400/40 text-zinc-300 font-mono text-[10px] uppercase tracking-widest flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> Clôturé
            </span>
          )}
          <StatusPill status={order.status} testid="admin-detail-status" />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Assignment / take charge */}
          <div className={`glass rounded-md p-6 ${locked ? "alert-untreated border" : ""}`} data-testid="assignment-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 grid place-items-center rounded-sm border ${order.assigned_admin_email ? "border-aurora-cyan/40 bg-aurora-cyan/10" : "border-signal-error/40 bg-signal-error/10"}`}>
                  <UserCheck className={`w-5 h-5 ${order.assigned_admin_email ? "text-aurora-cyan" : "text-signal-error"}`} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Ingénieur en charge</div>
                  <div className="font-display uppercase tracking-tight text-sm">
                    {order.assigned_admin_name
                      ? <>{order.assigned_admin_name}{order.assigned_admin_email === user?.email && <span className="text-aurora-cyan"> · vous</span>}</>
                      : <span className="text-signal-error">Non assigné</span>}
                  </div>
                </div>
              </div>
              {order.assigned_admin_email === user?.email ? (
                <button data-testid="unassign-btn" onClick={() => assign(true)} className="px-4 py-2 border border-white/15 rounded-sm font-mono text-[10px] uppercase tracking-widest hover:border-signal-error hover:text-signal-error transition-colors">
                  Me retirer
                </button>
              ) : (
                <button data-testid="assign-btn" onClick={() => assign(false)} className="flex items-center gap-2 px-5 py-2.5 bg-aurora-cyan text-white rounded-sm font-display font-bold uppercase tracking-wider text-xs hover:bg-cyan-400 transition-colors">
                  <UserPlus className="w-4 h-4" /> {order.assigned_admin_email ? "Reprendre" : "Prendre en charge"}
                </button>
              )}
            </div>
            {locked && (
              <div data-testid="claim-required-notice" className="mt-4 pt-4 border-t border-signal-error/30 flex items-start gap-2 text-signal-error">
                <Lock className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-xs leading-snug font-mono">
                  {order.assigned_admin_email
                    ? <>Cette commande est prise en charge par <b>{order.assigned_admin_name}</b>. Cliquez sur <b>Reprendre</b> pour la traiter.</>
                    : <>Cliquez sur <b>Prendre en charge</b> pour débloquer le traitement (upload, statut, étapes, livraison).</>}
                </p>
              </div>
            )}
          </div>

          {/* Editable progress stepper */}
          <div className="space-y-3">
            <ProgressStepper
              step={order.progress_step ?? stepFromStatus(order.status)}
              editable={!locked} onSet={setStage} note={null} busy={stageBusy}
            />
            <div className="glass rounded-md p-4">
              <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Message visible par le client (optionnel)</label>
              {order.client_lang && (
                <p data-testid="client-lang-hint" className="font-mono text-[10px] text-aurora-cyan/80 mb-2 flex items-center gap-1.5">
                  <span className="text-sm leading-none">{langFlag(order.client_lang)}</span>
                  Ce client utilise le <b className="text-aurora-cyan">{langLabel(order.client_lang)}</b> — écrivez dans cette langue (les messages ne sont pas traduits automatiquement).
                </p>
              )}
              <div className="flex gap-2">
                <input
                  data-testid="stage-note-input"
                  value={stageNote} onChange={(e) => setStageNote(e.target.value)}
                  placeholder="Ex: Optimisation cartographie en cours…"
                  className="flex-1 min-w-0 bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-aurora-cyan outline-none"
                />
                <button data-testid="save-stage-note" onClick={() => setStage(order.progress_step ?? stepFromStatus(order.status))} disabled={stageBusy || locked}
                  className="shrink-0 px-4 py-2 bg-aurora-cyan/10 border border-aurora-cyan/30 text-aurora-cyan font-mono text-xs uppercase tracking-wide rounded-sm hover:bg-aurora-cyan/20 transition-colors disabled:opacity-50">
                  Publier
                </button>
              </div>
            </div>
          </div>

          {/* Status controls */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">Changer le statut</h3>
            <div className="flex flex-wrap gap-2">
              {Object.keys(STATUS_META).map((s) => (
                <button key={s} data-testid={`set-status-${s}`} onClick={() => setStatus(s)} disabled={locked} className={`px-4 py-2 font-mono text-[11px] uppercase tracking-widest rounded-sm border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${order.status === s ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                  {STATUS_META[s].label}
                </button>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between gap-4 flex-wrap">
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
                {order.closed ? <>Demande clôturée{order.closed_by ? <> · par {order.closed_by}</> : null}</> : "Clôturer la demande une fois le dossier finalisé"}
              </div>
              <button
                data-testid="close-order-btn"
                onClick={toggleClose}
                disabled={closing || locked}
                className={`flex items-center gap-2 px-5 py-2.5 font-display font-bold uppercase tracking-wider text-xs rounded-sm transition-colors disabled:opacity-50 ${order.closed ? "border border-aurora-cyan/40 text-aurora-cyan hover:bg-aurora-cyan/10" : "bg-zinc-200 text-black hover:bg-white"}`}
              >
                {closing ? <Loader2 className="w-4 h-4 animate-spin" /> : order.closed ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                {order.closed ? "Rouvrir" : "Clôturer"}
              </button>
            </div>
          </div>

          {order.suggestion_confidence === "reference" && (
            <div className="glass rounded-md p-6 border-l-2 border-signal-processing" data-testid="reference-banner">
              <div className="flex items-center gap-2 mb-2">
                <Library className="w-5 h-5 text-signal-processing" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Référence trouvée dans la base</h3>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                Une solution validée existe pour la <b className="text-white">même version logicielle</b>, mais le fichier original du client est <b>différent</b> (adaptations spécifiques). <b className="text-signal-processing">Pas de livraison automatique</b> — utilisez cette référence comme base de départ, finalisez avec votre outil, puis uploadez le fichier traité.
              </p>
              <button onClick={downloadReference} data-testid="download-reference-btn" className="flex items-center gap-2 px-5 py-2.5 border border-signal-processing/50 text-signal-processing font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-signal-processing/10 transition-colors">
                <Download className="w-4 h-4" /> Télécharger la référence
              </button>
            </div>
          )}

          {order.status !== "completed" && !order.auto_processed && !(order.combination?.available && !order.combination_delivered) && (
            <div className="glass rounded-md p-5 border-l-2 border-aurora-cyan" data-testid="reprocess-card">
              <div className="flex items-center gap-2 mb-1">
                <RefreshCw className="w-5 h-5 text-aurora-cyan" />
                <h3 className="font-display text-base font-semibold uppercase tracking-tight">Re-tenter la livraison auto</h3>
              </div>
              <p className="font-mono text-[10px] text-zinc-500 leading-snug mb-4">
                Relance la reconnaissance du fichier original et la <b className="text-white">combinaison différentielle</b> des solutions de la bibliothèque. Utile si vous avez importé les solutions (ex. Stage 1 + FAP) <b>après</b> le dépôt de cette commande.
              </p>
              <button onClick={reprocess} disabled={reBusy || locked} data-testid="reprocess-btn" className="flex items-center gap-2 px-5 py-2.5 border border-aurora-cyan/50 text-aurora-cyan font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-aurora-cyan/10 transition-colors disabled:opacity-50">
                {reBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-tenter maintenant
              </button>
            </div>
          )}

          {order.combination?.available && !order.combination_delivered && (
            <div className="glass rounded-md p-6 border-l-2 border-aurora-purple" data-testid="combination-banner">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-5 h-5 text-aurora-purple" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">⚡ Combinaison auto disponible</h3>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                Le système a <b className="text-white">pré-assemblé</b> les solutions de votre base pour cette commande multi-services, par fusion différentielle sur le fichier original du client :
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {order.combination.sources.map((s, i) => (
                  <span key={i} className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-aurora-purple/40 text-aurora-purple rounded-sm">
                    {(s.services || []).join("+")} · {s.ref}
                  </span>
                ))}
              </div>
              <div className="font-mono text-[11px] text-zinc-400 mb-4 space-y-1">
                <div>{order.combination.changed_bytes} octets modifiés · famille {order.combination.family || "—"} · zones disjointes (0 conflit)</div>
                {order.combination.checksum_ok ? (
                  <div className="text-signal-success flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Checksum recalculé automatiquement</div>
                ) : (
                  <div className="text-signal-processing flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Checksum À VALIDER par le tuner avant livraison (téléchargez, corrigez dans WinOLS si besoin, puis livrez)</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={downloadCombination} data-testid="download-combination-btn" className="flex items-center gap-2 px-5 py-2.5 border border-aurora-purple/50 text-aurora-purple font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-aurora-purple/10 transition-colors">
                  <Download className="w-4 h-4" /> Télécharger (vérifier)
                </button>
                <button onClick={deliverCombination} disabled={comboBusy || locked} data-testid="deliver-combination-btn" className="flex items-center gap-2 px-5 py-2.5 bg-aurora-purple text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-purple-500 transition-colors disabled:opacity-50">
                  {comboBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Livrer cette combinaison
                </button>
              </div>
            </div>
          )}

          {(order.is_slave_order || order.encrypted) && (
            <div className="glass rounded-md p-5 border-l-2 border-amber-400" data-testid="admin-slave-banner">
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Fichier CRYPTÉ · client Slave{order.slave_tool ? ` · ${order.slave_tool}` : ""}
              </h3>
              <p className="text-sm text-zinc-200">
                Reconnaissance binaire impossible (fichier crypté). <b className="text-amber-400">Traitement 100% manuel</b> : téléchargez le fichier original, décryptez / calibrez / re-cryptez sur PC avec l'outil du client{order.slave_master_label ? ` (master : ${order.slave_master_label})` : ""}, puis livrez le fichier final ci-dessous.
              </p>
            </div>
          )}

          {order.ecu_report && (
            <div className="glass rounded-md p-5 border-l-2 border-signal-error" data-testid="admin-ecu-report">
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-signal-error mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Détection ECU signalée par le client
              </h3>
              <p className="text-sm text-zinc-200">
                Détecté : <b className="text-white">{order.ecu_report.detected || "—"}</b> · Corrigé : <b className="text-aurora-cyan">{order.ecu_report.correct_ecu || "(non précisé)"}</b>
              </p>
              {order.ecu_report.note && <p className="text-sm text-zinc-400 mt-1">{order.ecu_report.note}</p>}
            </div>
          )}

          {/* AI report */}
          <div className="glass rounded-md p-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Sparkles className="w-5 h-5 text-aurora-purple" />
              <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Reconnaissance & pré-analyse IA</h3>
              {order.ecu_confidence != null && (
                <span data-testid="admin-ai-confidence" className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm border flex items-center gap-1 ${order.ecu_confidence >= 85 ? "text-signal-success border-signal-success/40 bg-signal-success/10" : order.ecu_confidence >= 60 ? "text-aurora-cyan border-aurora-cyan/40 bg-aurora-cyan/10" : "text-signal-processing border-signal-processing/40 bg-signal-processing/10"}`}>
                  <Sparkles className="w-2.5 h-2.5" /> Premium · {order.ecu_confidence}% fiable
                </span>
              )}
            </div>
            <MarkdownLite text={order.ai_analysis || "Pas d'analyse."} />
          </div>

          <FileDiagnostics integrity={order.integrity} />

          <OrderChat orderId={order.id} role="admin" clientLang={order.client_lang || ""} />

          {/* Dyno (Stage 1) */}
          {order.services.includes("stage1") && (
            <div className="glass rounded-md p-6" data-testid="admin-dyno-card">
              <div className="flex items-center gap-2 mb-4">
                <Gauge className="w-5 h-5 text-aurora-cyan" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Dyno Stage 1</h3>
                {order.dyno?.source && (
                  <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-aurora-purple/10 text-aurora-purple">
                    {order.dyno.source === "ai" ? "estimation IA" : "validé atelier"}
                  </span>
                )}
              </div>
              {Number(dyno.base_hp) > 0 && (
                <DynoChart baseHp={Number(dyno.base_hp)} gainHp={Number(dyno.gain_hp)} baseNm={Number(dyno.base_nm)} gainNm={Number(dyno.gain_nm)} />
              )}
              <div className="grid grid-cols-2 gap-3 mt-5">
                {[["base_hp", "CV origine"], ["gain_hp", "Gain CV"], ["base_nm", "Nm origine"], ["gain_nm", "Gain Nm"]].map(([k, lbl]) => (
                  <div key={k}>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">{lbl}</label>
                    <input type="number" data-testid={`dyno-${k}`} className={inputCls} value={dyno[k]} onChange={(e) => setDyno({ ...dyno, [k]: e.target.value })} placeholder="0" />
                  </div>
                ))}
              </div>
              <button data-testid="save-dyno-btn" onClick={saveDyno} disabled={savingDyno || locked} className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-aurora-cyan text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm transition-colors disabled:opacity-60">
                {savingDyno ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer le dyno
              </button>
            </div>
          )}

          {/* Notes */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-1">Message de l'ingénieur</h3>
            <p className="font-mono text-[10px] text-zinc-500 mb-3">Particularités / instructions visibles par le client sur sa commande.</p>
            <textarea data-testid="admin-notes" rows={3} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Stage 1 + FAP off réalisés, checksum corrigé. Attention : remettre la cartographie d'origine avant contrôle technique." />
            <button data-testid="save-notes-btn" onClick={saveNotes} disabled={saving || locked} className="mt-3 flex items-center gap-2 px-5 py-2.5 border border-white/20 font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-white/5 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Files */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">Fichiers</h3>
            <button onClick={downloadOriginal} data-testid="admin-download-original" className="w-full flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-white/30 transition-colors mb-3">
              <FileDown className="w-5 h-5 text-zinc-400" />
              <div className="text-left min-w-0 flex-1">
                <div className="font-mono text-xs text-white truncate">{order.original_filename}</div>
                <div className="font-mono text-[10px] text-zinc-500">Original client</div>
              </div>
            </button>

            {autoEligible && (
              <label className="flex items-start gap-2.5 mb-3 p-3 rounded-sm border border-signal-success/30 bg-signal-success/5 cursor-pointer">
                <input type="checkbox" data-testid="save-solution-checkbox" checked={saveSolution} onChange={(e) => setSaveSolution(e.target.checked)} className="mt-0.5 accent-signal-success" />
                <span className="text-xs text-zinc-300 leading-snug">
                  <span className="font-mono uppercase tracking-widest text-signal-success text-[10px]">Solution réutilisable</span><br />
                  Enregistrer ce travail dans la bibliothèque — sur les prochains fichiers {order.vehicle_brand} / {order.ecu} compatibles, il sera {fullyAuto ? <>livré <b className="text-white">automatiquement</b></> : <>proposé à l'ingénieur (le <b className="text-white">patch</b> s'applique sur le fichier du client)</>}.
                </span>
              </label>
            )}

            <div className="mb-3 p-3 rounded-sm border border-aurora-purple/25 bg-aurora-purple/5">
              <label className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple mb-1.5 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Logiciel utilisé <span className="text-zinc-500 normal-case tracking-normal">(admin uniquement)</span>
              </label>
              <input data-testid="processed-tuned-with" value={tunedWith} onChange={(e) => setTunedWith(e.target.value)} placeholder="Ex. WinOLS"
                className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-purple focus:outline-none placeholder:text-zinc-600" />
              <p className="font-mono text-[9px] text-zinc-500 mt-1.5">Enrichit la base interne et trace l'outil de calibration en cas de problème. Jamais visible par le client.</p>
            </div>

            <input ref={fileInput} type="file" className="hidden" data-testid="processed-file-input" onChange={(e) => uploadProcessed(e.target.files[0])} />
            <button onClick={() => fileInput.current?.click()} disabled={uploading || locked} data-testid="upload-processed-btn" className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-cyan/40 bg-aurora-cyan/5 hover:bg-aurora-cyan/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin text-aurora-cyan" /> : <UploadCloud className="w-5 h-5 text-aurora-cyan" />}
              <div className="text-left">
                <div className="font-mono text-xs text-white">{order.processed_filename ? "Remplacer le fichier traité" : "Uploader le fichier traité"}</div>
                <div className="font-mono text-[10px] text-aurora-cyan">{order.processed_filename || "Marque la commande comme terminée"}</div>
              </div>
            </button>
            {order.processed_filename && (
              <div className="mt-3 flex items-center gap-2 font-mono text-[10px] text-signal-success">
                <CheckCircle2 className="w-4 h-4" /> {order.processed_filename}
              </div>
            )}
            {!order.engine_cancelled ? (
              <button onClick={() => cancelFile("engine")} disabled={locked} data-testid="cancel-engine-btn"
                className="mt-3 w-full py-2 rounded-sm border border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:border-signal-error/50 hover:text-signal-error transition-colors disabled:opacity-40">
                Je ne peux pas faire le fichier moteur (annuler ses crédits)
              </button>
            ) : (
              <div className="mt-3 flex items-center gap-2 font-mono text-[10px] text-signal-error"><AlertTriangle className="w-4 h-4" /> Moteur annulé · crédits remboursés</div>
            )}

            {order.secondary_filename && (
              <div className="mt-4 pt-4 border-t border-white/10" data-testid="admin-gearbox-card">
                <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple mb-3 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Boîte de vitesses ({order.secondary_credits || 0} cr.)</div>
                <button onClick={downloadSecondary} data-testid="admin-download-secondary" className="w-full flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-white/30 transition-colors mb-3">
                  <Download className="w-5 h-5 text-zinc-400" />
                  <div className="text-left min-w-0"><div className="font-mono text-xs text-white truncate">{order.secondary_filename}</div><div className="font-mono text-[10px] text-zinc-500">Fichier boîte original du client</div></div>
                </button>
                {order.secondary_cancelled ? (
                  <div className="flex items-center gap-2 font-mono text-[10px] text-signal-error"><AlertTriangle className="w-4 h-4" /> Boîte annulée · crédits remboursés</div>
                ) : (
                  <>
                    <label data-testid="upload-secondary-btn" className="w-full flex items-center gap-3 p-3 rounded-sm border border-dashed border-aurora-purple/40 bg-aurora-purple/5 hover:bg-aurora-purple/10 transition-colors cursor-pointer">
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin text-aurora-purple" /> : <UploadCloud className="w-5 h-5 text-aurora-purple" />}
                      <div className="text-left"><div className="font-mono text-xs text-white">{order.secondary_processed_filename ? "Remplacer le fichier boîte traité" : "Uploader le fichier boîte traité"}</div><div className="font-mono text-[10px] text-aurora-purple">{order.secondary_processed_filename || "Livré séparément au client"}</div></div>
                      <input type="file" className="hidden" disabled={uploading || locked} onChange={(e) => uploadSecondary(e.target.files[0])} />
                    </label>
                    <button onClick={() => cancelFile("secondary")} disabled={locked} data-testid="cancel-secondary-btn"
                      className="mt-2 w-full py-2 rounded-sm border border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:border-signal-error/50 hover:text-signal-error transition-colors disabled:opacity-40">
                      Je ne peux pas faire la boîte (rembourser ses crédits)
                    </button>
                  </>
                )}
              </div>
            )}
            {order.solution_ref && (
              <div className="mt-3 p-2.5 rounded-sm border border-aurora-cyan/30 bg-aurora-cyan/5 space-y-2.5" data-testid="order-solution-ref">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-aurora-cyan shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Fichier base livré</div>
                    <div className="font-mono text-xs font-bold text-aurora-cyan">{order.solution_ref}</div>
                  </div>
                </div>
                {order.defect_reported ? (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 p-2 rounded-sm bg-signal-error/10 border border-signal-error/40" data-testid="defect-flag">
                      <ShieldAlert className="w-3.5 h-3.5 text-signal-error shrink-0 mt-0.5" />
                      <span className="font-mono text-[10px] text-signal-error leading-snug">Défaut signalé{order.defect_reason ? ` · ${order.defect_reason}` : ""}</span>
                    </div>
                    <button data-testid="clear-defect-btn" onClick={clearDefect} disabled={defectBusy || locked}
                      className="w-full flex items-center justify-center gap-2 py-2 border border-white/15 rounded-sm font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-signal-success hover:text-signal-success transition-colors disabled:opacity-50">
                      {defectBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Lever le défaut
                    </button>
                  </div>
                ) : (
                  <button data-testid="report-defect-btn" onClick={reportDefect} disabled={defectBusy || locked}
                    className="w-full flex items-center justify-center gap-2 py-2 border border-signal-error/30 rounded-sm font-mono text-[10px] uppercase tracking-widest text-signal-error hover:bg-signal-error hover:text-white transition-colors disabled:opacity-50">
                    {defectBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />} Signaler un défaut
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Compatible candidates from the library */}
          {candidates.length > 0 && (
            <div className="glass rounded-md p-6" data-testid="candidates-panel">
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-5 h-5 text-aurora-purple" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">Candidats compatibles</h3>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-zinc-500">{candidates.length}</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-500 mb-4 leading-snug">
                Fichiers de la bibliothèque compatibles avec l'original du client. Choisissez celui à livrer — la <b className="text-aurora-cyan">réf.</b> est tracée sur la commande.
              </p>
              <div className="space-y-2.5">
                {candidates.map((c) => {
                  const isApplied = order.applied_solution_id === c.id;
                  const exact = c.match === "exact";
                  return (
                    <div key={c.id} data-testid={`candidate-${c.id}`} className={`flex items-center gap-3 p-3 rounded-sm border ${isApplied ? "border-signal-success/50 bg-signal-success/5" : "border-white/10 hover:border-white/25"} transition-colors`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-bold text-aurora-cyan">{c.ref || "—"}</span>
                          <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${exact ? "bg-signal-success/15 text-signal-success" : "bg-signal-processing/15 text-signal-processing"}`}>
                            {exact ? "Exact" : "Référence"}
                          </span>
                          {c.safest && <span data-testid={`safest-${c.id}`} className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-signal-success/20 text-signal-success"><ShieldCheck className="w-3 h-3" /> Le plus sûr</span>}
                          {isApplied && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-aurora-cyan/15 text-aurora-cyan">Livré</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <ReliabilityPill reliability={c.reliability} deliveries={c.deliveries} />
                        </div>
                        <div className="font-mono text-[10px] text-zinc-500 truncate mt-1">
                          {(c.brand || "—")}{c.ecu ? ` · ${c.ecu}` : ""} · {(c.services || []).join(", ")}
                        </div>
                      </div>
                      <button
                        data-testid={`apply-candidate-${c.id}`}
                        onClick={() => applySolution(c.id, c.ref)}
                        disabled={applyingId === c.id || locked}
                        className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-aurora-cyan text-black font-mono text-[10px] uppercase tracking-widest rounded-sm hover:bg-cyan-300 transition-colors disabled:opacity-50"
                      >
                        {applyingId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        {isApplied ? "Re-livrer" : "Livrer"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Solutions */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">Solutions · {order.total_credits} cr.</h3>
            <div className="flex flex-wrap gap-2">
              {order.services.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border border-aurora-cyan/30 text-aurora-cyan rounded-sm">{s}</span>
              ))}
            </div>
            {order.customer_note && <p className="mt-4 text-xs text-zinc-400 border-t border-white/5 pt-3"><span className="text-zinc-600">Note client: </span>{order.customer_note}</p>}
          </div>

          {/* Info */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">Détails</h3>
            <div className="space-y-2">
              {info.map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{k}</span>
                  <span className="text-zinc-200 truncate">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};
